import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, Download, ChevronLeft, ChevronRight, Search, X, Eye } from 'lucide-react';
import apiService from '../services/apiService';

const ROLE_LEVEL = { office_staff: 1, admin: 2, super_user: 3 };

const ACTION_COLOURS = {
  CREATE:              'bg-emerald-100 text-emerald-700',
  UPDATE:              'bg-blue-100 text-blue-700',
  VOID:                'bg-red-100 text-red-700',
  CONFIRM:             'bg-teal-100 text-teal-700',
  AUTO_CONFIRM:        'bg-cyan-100 text-cyan-700',
  TURNING_21_FLAGGED:  'bg-amber-100 text-amber-700',
  MEMBER_PROMOTED_AT_21: 'bg-purple-100 text-purple-700',
  PROMOTION_REVERSED:  'bg-orange-100 text-orange-700',
};

const ACTIONS = [
  'CREATE', 'UPDATE', 'VOID', 'CONFIRM', 'AUTO_CONFIRM',
  'TURNING_21_FLAGGED', 'MEMBER_PROMOTED_AT_21', 'PROMOTION_REVERSED',
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function DiffModal({ log, onClose }) {
  if (!log) return null;

  const oldV = log.old_values ? (typeof log.old_values === 'string' ? JSON.parse(log.old_values) : log.old_values) : null;
  const newV = log.new_values ? (typeof log.new_values === 'string' ? JSON.parse(log.new_values) : log.new_values) : null;

  const allKeys = Array.from(new Set([...Object.keys(oldV || {}), ...Object.keys(newV || {})]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-900">Audit Detail</p>
            <p className="text-xs text-slate-400 mt-0.5">{fmtDate(log.created_at)} · {log.action} · {log.table_name}{log.record_id ? ` #${log.record_id}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-1">Performed By</p>
              <p className="font-semibold text-slate-800">{log.performed_by || '—'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-1">Role</p>
              <p className="font-semibold text-slate-800">{log.role || '—'}</p>
            </div>
            {log.reason && (
              <div className="bg-amber-50 rounded-lg p-3 col-span-2">
                <p className="text-xs text-amber-600 font-medium uppercase tracking-wide mb-1">Reason</p>
                <p className="text-slate-800">{log.reason}</p>
              </div>
            )}
          </div>

          {/* Changes diff */}
          {allKeys.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Changes</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
                <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <span>Field</span><span>Before</span><span>After</span>
                </div>
                {allKeys.map(key => {
                  const before = oldV?.[key];
                  const after  = newV?.[key];
                  const changed = JSON.stringify(before) !== JSON.stringify(after);
                  return (
                    <div key={key} className={`grid grid-cols-3 px-4 py-2 border-t border-slate-100 ${changed ? 'bg-blue-50/50' : ''}`}>
                      <span className="text-slate-500 font-mono text-xs self-center">{key}</span>
                      <span className={`text-xs font-mono break-all ${changed && before !== undefined ? 'text-red-600 line-through' : 'text-slate-400'}`}>
                        {before !== undefined ? String(typeof before === 'object' ? JSON.stringify(before) : before) : <span className="italic text-slate-300">—</span>}
                      </span>
                      <span className={`text-xs font-mono break-all ${changed && after !== undefined ? 'text-emerald-700 font-semibold' : 'text-slate-400'}`}>
                        {after !== undefined ? String(typeof after === 'object' ? JSON.stringify(after) : after) : <span className="italic text-slate-300">—</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AuditLogPage() {
  const { user } = useAuth();
  const isSuperUser = (ROLE_LEVEL[user?.role] || 0) >= ROLE_LEVEL.super_user;

  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [detail, setDetail]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    performedBy: '',
    action:      '',
    actorType:   '',
    dateFrom:    '',
    dateTo:      '',
  });
  const [applied, setApplied] = useState(filters);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (applied.performedBy) params.set('performedBy', applied.performedBy);
      if (applied.action)      params.set('action', applied.action);
      if (applied.actorType)   params.set('actorType', applied.actorType);
      if (applied.dateFrom)    params.set('dateFrom', applied.dateFrom);
      if (applied.dateTo)      params.set('dateTo', applied.dateTo);
      const data = await apiService.request(`/audit/logs?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  }, [page, applied]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setApplied(filters); setPage(1); };
  const clearFilters = () => {
    const empty = { performedBy: '', action: '', actorType: '', dateFrom: '', dateTo: '' };
    setFilters(empty); setApplied(empty); setPage(1);
  };
  const hasFilters = Object.values(applied).some(Boolean);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (applied.performedBy) params.set('performedBy', applied.performedBy);
      if (applied.action)      params.set('action', applied.action);
      if (applied.actorType)   params.set('actorType', applied.actorType);
      if (applied.dateFrom)    params.set('dateFrom', applied.dateFrom);
      if (applied.dateTo)      params.set('dateTo', applied.dateTo);

      const token = localStorage.getItem('token');
      const res = await fetch(`/api/audit/logs/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  if (!isSuperUser) {
    return (
      <div className="p-8 flex flex-col items-center justify-center text-center">
        <Shield className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-slate-500 font-medium">Super user access required</p>
      </div>
    );
  }

  const inputCls = 'px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Audit Log</h1>
            <p className="text-sm text-slate-500">{total.toLocaleString()} records</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <input
            type="text" placeholder="Performed by…" value={filters.performedBy}
            onChange={e => setFilters(f => ({ ...f, performedBy: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
            className={inputCls}
          />
          <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} className={inputCls}>
            <option value="">All actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filters.actorType} onChange={e => setFilters(f => ({ ...f, actorType: e.target.value }))} className={inputCls}>
            <option value="">All actors</option>
            <option value="user">Human</option>
            <option value="system">System / Cron</option>
          </select>
          <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} className={inputCls} />
          <input type="date" value={filters.dateTo}   onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}   className={inputCls} />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={applyFilters}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Search className="w-3.5 h-3.5" /> Apply
          </button>
          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm rounded-lg transition-colors">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No audit records found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Timestamp', 'Action', 'Performed By', 'Role', 'Table', 'Record', 'Reason', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map(log => {
                  const actionCls = ACTION_COLOURS[log.action] || 'bg-slate-100 text-slate-600';
                  const isSystem = log.actor_type === 'system';
                  return (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap font-mono">{fmtDate(log.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${actionCls}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${isSystem ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                          {log.performed_by || '—'}
                        </span>
                        {isSystem && <span className="ml-1 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">system</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{log.role || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 font-mono">{log.table_name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{log.record_id || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[200px] truncate" title={log.reason || undefined}>
                        {log.reason || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {(log.old_values || log.new_values) && (
                          <button onClick={() => setDetail(log)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View changes">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {detail && <DiffModal log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
